import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef
} from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Box, Button, FormControl } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import {
  CLEAR_DISTRIBUTOR_DETAIL,
  getDistributorParameters
} from 'src/store/actions/distributorActions';
import Autocomplete from 'src/components/Autocomplete/Autocomplete';
import {
  selectClientsList,
  selectClientBankAccountByPriority,
  selectClientBankAccountDetail
} from 'src/store/selectors/clientSelectors';
import {
  CLEAR_CLIENT_OPTIONS,
  LOAD_CLIENTS,
  loadAllClients,
  loadClientBankAccountInformation
} from 'src/store/actions/clientActions';
import AlertWithLink from 'src/components/Alert/AlertWithLink/AlertWithLink';
import { hasBankInfoCompleted, hasAddressCompleted } from 'src/utils/common';
import { selectAddressByType } from 'src/utils/address';
import AddressType from 'src/components/AddressDialog/AddressEnums';
import BankAccountPriority from 'src/utils/constants/bank-accounts';
import { DatePicker } from '@mui/x-date-pickers';
import momentLocalTimezone from 'src/utils/momentLocalTimezone';
import { Link } from 'react-router-dom';
import makeInitialState from './initialState';
import validationSchema from './schema';
import useStyles from '../styles';
import ClientValidationAlert from './ClientValidationAlert';
import ClientBuroValidationAlert from './ClientBuroValidationAlert';

const StepOne = ({ open, data, handleNext, resetRef }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const initialState = useMemo(() => makeInitialState(data), [data]);
  const [openAlert, setOpenAlert] = useState(false);
  const [hasClientValidationWarning, setHasClientValidationWarning] = useState(
    false
  );
  const [selectedClientId, setSelectedClientId] = useState(null);
  const setFieldValueRef = useRef(null);

  const clientList = useSelector(selectClientsList);
  const primaryBankAccount = useSelector(
    selectClientBankAccountByPriority(BankAccountPriority.PRIMARY)
  );
  const allBankAccounts = useSelector(
    state => selectClientBankAccountDetail(state)?.bankAccounts ?? []
  );

  const clientListWithoutContractClient = useMemo(() => {
    if (!Array.isArray(clientList) || !data?.clientId) return [];

    return clientList.filter(client => client.id !== Number(data.clientId));
  }, [clientList, data?.clientId]);

  const loadingClients = useSelector(isLoadingSelector([LOAD_CLIENTS.action]));
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      dispatch({ type: CLEAR_CLIENT_OPTIONS });
      dispatch({ type: CLEAR_DISTRIBUTOR_DETAIL });
      dispatch(loadAllClients());
    }
  }, [open]);

  useEffect(() => {
    if (selectedClientId) {
      dispatch(loadClientBankAccountInformation(selectedClientId));
    }
  }, [selectedClientId, dispatch]);

  useEffect(() => {
    if (selectedClientId && setFieldValueRef.current) {
      if (primaryBankAccount) {
        setFieldValueRef.current('bankInformation', primaryBankAccount);
      }

      const hasAnyCompleteBankInfo = allBankAccounts.some(hasBankInfoCompleted);
      if (hasAnyCompleteBankInfo) {
        setOpenAlert(false);
      }
    }
  }, [primaryBankAccount, allBankAccounts, selectedClientId]);

  const onNext = useCallback(
    values => {
      handleNext(values);
      if (values.distributorModel) {
        dispatch(getDistributorParameters(values.distributorModel.id));
      }
    },
    [handleNext]
  );

  const onCloseAlert = useCallback(() => {
    setOpenAlert(false);
  }, []);

  const handleClientValidationWarningChange = useCallback(hasWarning => {
    setHasClientValidationWarning(hasWarning);
  }, []);

  return (
    <Formik
      initialValues={initialState}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={onNext}
    >
      {({
        errors,
        handleSubmit,
        touched,
        values,
        setFieldValue,
        resetForm
      }) => {
        setFieldValueRef.current = setFieldValue;
        resetRef.current = () => resetForm();

        return (
          <form onSubmit={handleSubmit}>
            <Box className={classes.container}>
              <Box display="flex" flexDirection="column" gap={2}>
                <AlertWithLink
                  severity="warning"
                  text={t('INFO.LUCK_BANK_INFORMATION')}
                  open={openAlert}
                  link={
                    <Link
                      to={`/clients/detail/${values.cededClientId}/billing`}
                    >
                      {t(
                        'CONTRACT_VIEW.AUTOMATIC_PAYMENT_BANK_ACCOUNT_REDIRECT'
                      )}
                    </Link>
                  }
                  handleClose={onCloseAlert}
                />

                {values.cededClientId && selectedClientId && (
                  <>
                    <ClientValidationAlert
                      clientId={values.cededClientId}
                      onWarningStateChange={handleClientValidationWarningChange}
                    />
                    <ClientBuroValidationAlert
                      clientId={values.cededClientId}
                    />
                  </>
                )}
              </Box>
              <FormControl
                fullWidth
                error={Boolean(touched.cededClientId && errors.cededClientId)}
              >
                <Autocomplete
                  options={clientListWithoutContractClient}
                  id="cededClientId"
                  label={t('COMMON.CLIENT')}
                  isLoading={loadingClients}
                  getOptionLabel={option => {
                    return option.fullName
                      ? `${option.fullName} - ID:${option.id}`
                      : '';
                  }}
                  value={clientList.find(
                    client => client.id === values.cededClientId
                  )}
                  fullWidth
                  sx={{ marginBottom: 2, marginTop: 2 }}
                  onChange={(_e, value) => {
                    if (!value) {
                      setFieldValue('cededClientId', '');
                      setFieldValue('cededClientName', '');
                      setFieldValue('cededClientPersonType', '');
                      setFieldValue('avals', []);
                      setFieldValue('bankInformation', {});
                      setSelectedClientId(null);
                      setOpenAlert(false);
                      return;
                    }

                    setFieldValue('cededClientId', value.id);
                    setFieldValue('cededClientName', value.fullName ?? '');
                    setFieldValue(
                      'cededClientPersonType',
                      value.personType ?? ''
                    );
                    setFieldValue('avals', []);
                    setFieldValue('bankInformation', {});
                    setSelectedClientId(value.id);
                    setOpenAlert(true);

                    const address =
                      selectAddressByType(value.addresses, AddressType.WORK) ||
                      {};
                    if (hasAddressCompleted(address)) {
                      setFieldValue('equipmentLocation', {
                        street: address.street,
                        insideHomeNumber: address.insideHomeNumber,
                        externalHomeNumber: address.externalHomeNumber,
                        colonia: address.colonia,
                        city: address.city,
                        state: address.state,
                        municipality: address.municipality,
                        zipCode: address.zipCode
                      });
                      setFieldValue('isExistingAddress', true);
                    }
                  }}
                  helperText={touched.cededClientId && t(errors.cededClientId)}
                  error={Boolean(touched.cededClientId && errors.cededClientId)}
                />
              </FormControl>
              <DatePicker
                label={t(
                  'CONTRACT_VIEW.CEDE_CONTRACT_FORM.STEP_ONE_INPUTS.CEDE_DATE'
                )}
                format="MM/YYYY"
                value={values.cedeDate}
                minDate={momentLocalTimezone().add(1, 'month')}
                maxDate={momentLocalTimezone(data.endDate)
                  .subtract(1, 'month')
                  .endOf('month')}
                onChange={newValue => {
                  setFieldValue('cedeDate', momentLocalTimezone(newValue));
                }}
                slotProps={{
                  textField: {
                    sx: { width: '100%', margin: '16px 0' },
                    error: Boolean(touched.cedeDate && errors.cedeDate),
                    helperText: touched.cedeDate && t(errors.cedeDate),
                    FormHelperTextProps: { sx: { color: 'red' } },
                    InputProps: { autoComplete: 'off' }
                  }
                }}
              />
            </Box>
            <Box
              className={classes.box}
              display="flex"
              justifyContent="flex-end"
            >
              <Button
                className={clsx(classes.buttons, classes.buttonMain)}
                type="submit"
                color="primary"
                variant="outlined"
                disabled={
                  !values.cededClientId ||
                  openAlert ||
                  hasClientValidationWarning
                }
              >
                {t('COMMON.NEXT')}
              </Button>
            </Box>
          </form>
        );
      }}
    </Formik>
  );
};
StepOne.propTypes = {
  open: PropTypes.bool,
  data: PropTypes.object,
  resetRef: PropTypes.object,
  handleNext: PropTypes.func
};

export default StepOne;
